import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Box, Typography, Paper, Chip, Link, ButtonGroup, Button, Divider, TextField, MenuItem } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import texasParks from '../../data/texasParks.json';

// Fix markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 🔥 Auto classify
const enrichPark = (park) => {
  if (park.name.includes('Natural Area')) {
    return { ...park, type: 'natural_area', displayType: 'Natural Area' };
  }
  if (park.name.includes('Historic')) {
    return { ...park, type: 'historic_site', displayType: 'Historic Site' };
  }
  return { ...park, type: 'state_park', displayType: 'State Park' };
};

const enrichedParks = texasParks.map(enrichPark);

const parkTypeLabels = {
  state_park: 'State Park',
  natural_area: 'State Natural Area',
  historic_site: 'Historic Site',
};

const parkTypeOptions = ['All Types', ...new Set(enrichedParks.map((park) => park.type))];

// Enhanced Popup Component
const ParkPopup = ({ park }) => (
  <Box
    sx={{
      width: '300px',
      background: '#ffffff',
      color: '#333',
    }}
  >
    {/* Park Name */}
    <Typography
      variant="h6"
      sx={{
        mb: 1.5,
        fontWeight: 700,
        fontSize: '1.1rem',
        letterSpacing: '-0.5px',
        color: '#2c3e50'
      }}
    >
      🌲 {park.name}
    </Typography>

    {/* County Info */}
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, minWidth: '60px', color: '#555', fontSize: '0.9rem' }}>📍 County:</Typography>
      <Typography variant="body2" sx={{ color: '#2c3e50', fontSize: '0.9rem' }}>{park.county}</Typography>
    </Box>

    {/* Park Type Badge */}
    <Box sx={{ mb: 1.5 }}>
      <Chip
        label={park.displayType}
        sx={{
          background: '#f0f0f0',
          color: '#2c3e50',
          fontWeight: 600,
          Mr: 0.5,
          height: '28px',
          border: '1px solid #ddd'
        }}
      />
    </Box>

    {/* Visit Status */}
    <Box sx={{ mb: 1.5 }}>
      <Chip
        label={park.visited ? '✅ Visited' : '🎯 Not Yet'}
        sx={{
          background: park.visited
            ? 'linear-gradient(135deg, #bbd6bc 0%, #a8c5ba 100%)'
            : 'linear-gradient(135deg, #f4aeae 0%, #dcc6a0 100%)',
          color: '#333',
          fontWeight: 600,
          height: '28px'
        }}
      />
    </Box>

    {/* Visit Date */}
    {park.visited && (
      <Box sx={{ mb: 1.5, p: 1, background: '#f5f5f5', borderRadius: '4px' }}>
        <Typography variant="caption" sx={{ fontSize: '0.85rem', color: '#666' }}>
          📅 First Visit: {new Date(park.visitDate).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </Typography>
      </Box>
    )}

    <Divider sx={{ my: 1.5, borderColor: '#e0e0e0' }} />

    {/* Links Section */}
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
      {/* Website Link */}
      <Link
        href={park.websiteUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'inline-block',
          px: 1.5,
          py: 0.5,
          background: '#96c8ba',
          color: '#000000 !important',
          borderRadius: '4px',
          textDecoration: 'none !important',
          fontWeight: 600,
          fontSize: '0.85rem',
          border: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: '#5a8a7e',
            transform: 'translateY(-1px)',
            color: '#000000 !important'
          }
        }}
      >
        🌐 Learn More
      </Link>

      {/* Instagram Link */}
      {park.instagramUrl && (
        <Link
          href={park.instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-block',
            px: 1.5,
            py: 0.5,
            background: '#f9858d',
            color: '#000000 !important',
            borderRadius: '4px',
            textDecoration: 'none !important',
            fontWeight: 600,
            fontSize: '0.85rem',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: '#b73e45',
              transform: 'translateY(-1px)',
              color: '#000000 !important'
            }
          }}
        >
          📸 Instagram Post
        </Link>
      )}
    </Box>
  </Box>
);

const TXStateParks = () => {
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [view, setView] = useState('map');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPark, setSelectedPark] = useState(null);
  const mapRef = useRef(null);
  const listItemRefs = useRef({});

  useEffect(() => {
    if (view === 'map' && mapRef.current) {
      const timeout = setTimeout(() => mapRef.current.invalidateSize(), 150);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [view]);

  useEffect(() => {
    if (selectedPark && mapRef.current) {
      mapRef.current.flyTo([selectedPark.lat, selectedPark.lng], 8, { duration: 0.9 });
      const selectedEl = listItemRefs.current[selectedPark.name];
      if (selectedEl) {
        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedPark]);

  const filteredParks = enrichedParks.filter((park) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch =
      park.name.toLowerCase().includes(normalizedSearch) ||
      park.county.toLowerCase().includes(normalizedSearch) ||
      park.displayType.toLowerCase().includes(normalizedSearch);

    if (!matchesSearch) return false;
    if (typeFilter !== 'All Types' && park.type !== typeFilter) return false;
    if (filter === 'visited') return park.visited;
    if (filter === 'not-visited') return !park.visited;
    return true; // 'all'
  });

  const visitedCount = enrichedParks.filter(p => p.visited).length;
  const totalCount = enrichedParks.length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 4 }}>
      <Typography variant="h3" align="center" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.2rem' } }}>
        TX State Parks Adventure Map
      </Typography>

      <Typography align="center" sx={{ mb: 3 }}>
        We've visited <strong>{visitedCount}</strong> out of <strong>{totalCount}</strong> parks!
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search parks..."
          size="small"
          sx={{ width: { xs: '100%', sm: 320 } }}
        />

        <TextField
          select
          label="Type"
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ width: { xs: '100%', sm: 220 } }}
        >
          {parkTypeOptions.map((type) => (
            <MenuItem key={type} value={type}>
              {type === 'All Types' ? 'All Types' : parkTypeLabels[type] || type}
            </MenuItem>
          ))}
        </TextField>

        <ButtonGroup variant="outlined" size="small">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'contained' : 'outlined'}
            sx={{ 
              backgroundColor: filter === 'all' ? '#4ade80' : 'transparent',
              color: filter === 'all' ? 'white' : 'inherit',
              borderColor: '#4ade80',
              '&:hover': {
                backgroundColor: filter === 'all' ? '#22c55e' : 'rgba(74, 222, 128, 0.1)'
              }
            }}
          >
            All Parks
          </Button>
          <Button
            onClick={() => setFilter('visited')}
            variant={filter === 'visited' ? 'contained' : 'outlined'}
            sx={{ 
              backgroundColor: filter === 'visited' ? '#4ade80' : 'transparent',
              color: filter === 'visited' ? 'white' : 'inherit',
              borderColor: '#4ade80',
              '&:hover': {
                backgroundColor: filter === 'visited' ? '#22c55e' : 'rgba(74, 222, 128, 0.1)'
              }
            }}
          >
            Visited
          </Button>
          <Button
            onClick={() => setFilter('not-visited')}
            variant={filter === 'not-visited' ? 'contained' : 'outlined'}
            sx={{ 
              backgroundColor: filter === 'not-visited' ? '#4ade80' : 'transparent',
              color: filter === 'not-visited' ? 'white' : 'inherit',
              borderColor: '#4ade80',
              '&:hover': {
                backgroundColor: filter === 'not-visited' ? '#22c55e' : 'rgba(74, 222, 128, 0.1)'
              }
            }}
          >
            Not Yet
          </Button>
        </ButtonGroup>

        <ButtonGroup variant="outlined" size="small">
          <Button
            onClick={() => setView('map')}
            variant={view === 'map' ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: view === 'map' ? '#10b981' : 'transparent',
              color: view === 'map' ? 'white' : 'inherit',
              borderColor: '#10b981',
              '&:hover': {
                backgroundColor: view === 'map' ? '#059669' : 'rgba(16, 185, 129, 0.1)'
              }
            }}
          >
            Map View
          </Button>
          <Button
            onClick={() => setView('list')}
            variant={view === 'list' ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: view === 'list' ? '#10b981' : 'transparent',
              color: view === 'list' ? 'white' : 'inherit',
              borderColor: '#10b981',
              '&:hover': {
                backgroundColor: view === 'list' ? '#059669' : 'rgba(16, 185, 129, 0.1)'
              }
            }}
          >
            List View
          </Button>
        </ButtonGroup>
      </Box>

      <Typography align="center" sx={{ mb: 2, color: '#555' }}>
        Showing <strong>{filteredParks.length}</strong> parks in {view === 'map' ? 'map' : 'list'} view.
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 2, minHeight: { xs: 400, sm: 600 }, height: { xs: 400, sm: 600 } }}>
        {view === 'map' ? (
          <MapContainer
            center={[31, -99]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            whenCreated={(map) => {
              mapRef.current = map;
              setTimeout(() => {
                map.invalidateSize();
                if (selectedPark) {
                  map.flyTo([selectedPark.lat, selectedPark.lng], 8, { duration: 0.9 });
                }
              }, 150);
            }}
          >
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {filteredParks.map((park, i) => (
              <Marker
                key={i}
                position={[park.lat, park.lng]}
                eventHandlers={{ click: () => setSelectedPark(park) }}
                icon={L.icon({
                  iconUrl: park.visited
                    ? 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png'
                    : 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png',
                  iconSize: [25, 41],
                  iconAnchor: [12, 41],
                  popupAnchor: [1, -34]
                })}
              >
                <Popup>
                  <ParkPopup park={park} />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        ) : (
          <Box sx={{ p: 2, maxHeight: { xs: 400, sm: 600 }, overflowY: 'auto', backgroundColor: '#fafafa' }}>
            {filteredParks.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: '#666', py: 6 }}>
                No parks match this filter.
              </Typography>
            ) : (
              filteredParks.map((park) => (
                <Paper
                  key={park.name}
                  ref={(el) => { listItemRefs.current[park.name] = el; }}
                  onClick={() => {
                    setSelectedPark(park);
                    setView('map');
                  }}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    borderColor: selectedPark?.name === park.name ? '#10b981' : '#e0e0e0',
                    borderWidth: selectedPark?.name === park.name ? 2 : 1,
                    cursor: 'pointer',
                    backgroundColor: selectedPark?.name === park.name ? 'rgba(16, 185, 129, 0.08)' : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700, color: '#1f2937' }}>
                        🌲 {park.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4b5563' }}>
                        {park.county} • {park.displayType}
                      </Typography>
                    </Box>
                    <Chip
                      label={park.visited ? '✅ Visited' : '🎯 Not Yet'}
                      sx={{
                        alignSelf: 'center',
                        background: park.visited
                          ? 'linear-gradient(135deg, #bbd6bc 0%, #a8c5ba 100%)'
                          : 'linear-gradient(135deg, #f4aeae 0%, #dcc6a0 100%)',
                        color: '#333',
                        fontWeight: 600,
                        height: '28px'
                      }}
                    />
                  </Box>

                  {park.visited && park.visitDate && (
                    <Typography variant="body2" sx={{ mt: 1, color: '#6b7280' }}>
                      📅 First Visit: {new Date(park.visitDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Link
                      href={park.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        background: '#96c8ba',
                        color: '#000000 !important',
                        borderRadius: '4px',
                        textDecoration: 'none !important',
                        fontWeight: 600,
                        fontSize: '0.85rem'
                      }}
                    >
                      🌐 Learn More
                    </Link>
                    {park.instagramUrl && (
                      <Link
                        href={park.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          background: '#f9858d',
                          color: '#000000 !important',
                          borderRadius: '4px',
                          textDecoration: 'none !important',
                          fontWeight: 600,
                          fontSize: '0.85rem'
                        }}
                      >
                        📸 Instagram
                      </Link>
                    )}
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TXStateParks;